import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Select, Segmented, Table, Progress, Tag, Modal, Button } from 'antd';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import {
  DollarOutlined, TeamOutlined, UserAddOutlined, LoginOutlined,
  TrophyOutlined, RiseOutlined, ShopOutlined, FileTextOutlined,
  FullscreenOutlined, DragOutlined,
} from '@ant-design/icons';
import GridLayout, { WidthProvider, type LayoutItem } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import StatCard from '../../components/dashboards/StatCard';
import DateRangeFilter from '../../components/dateRangeFilter';
import usePermissions from '../../hooks/usePermissions';
import {
  useGetSuperAdminKpiQuery, useGetRevenueBreakdownQuery, useGetRevenueTrendQuery,
  useGetClubLeaderboardQuery, useGetSalesLeaderboardQuery, useGetCrmRegistrationsQuery,
  useGetPtUtilizationQuery,
} from '../../services/dashboard';
import './styles.scss';

// WidthProvider auto-measures the container width (v1-compatible API).
const ReactGridLayout = WidthProvider(GridLayout);

// Default positions (12-col grid). x/y = cell, w/h = span. minW/minH stop a
// drag-resize from squeezing a card so small its content gets cut off.
const DEFAULT_LAYOUT: LayoutItem[] = [
  { i: 'stat-revenue',     x: 0, y: 0,  w: 3,  h: 2, minW: 2, minH: 2 },
  { i: 'stat-checkins',    x: 3, y: 0,  w: 3,  h: 2, minW: 2, minH: 2 },
  { i: 'stat-active',      x: 6, y: 0,  w: 3,  h: 2, minW: 2, minH: 2 },
  { i: 'stat-joins',       x: 9, y: 0,  w: 3,  h: 2, minW: 2, minH: 2 },
  { i: 'chart-trend',      x: 0, y: 2,  w: 7,  h: 5, minW: 4, minH: 4 },
  { i: 'chart-breakdown',  x: 7, y: 2,  w: 5,  h: 5, minW: 3, minH: 4 },
  { i: 'club-leaderboard', x: 0, y: 7,  w: 12, h: 4, minW: 6, minH: 3 },
  { i: 'panel-sales',      x: 0, y: 11, w: 6,  h: 4, minW: 4, minH: 3 },
  { i: 'panel-crm',        x: 6, y: 11, w: 3,  h: 4, minW: 2, minH: 3 },
  { i: 'panel-pt',         x: 9, y: 11, w: 3,  h: 4, minW: 2, minH: 3 },
];

// Phone layout: its own 2-col grid (stat cards sit 2-per-row, everything
// else spans both columns) — still react-grid-layout, so every card keeps
// the same drag-to-reorder handle as desktop, just on a narrower grid.
const MOBILE_LAYOUT: LayoutItem[] = [
  { i: 'stat-revenue',     x: 0, y: 0,  w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'stat-checkins',    x: 1, y: 0,  w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'stat-active',      x: 0, y: 2,  w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'stat-joins',       x: 1, y: 2,  w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'chart-trend',      x: 0, y: 4,  w: 2, h: 5, minW: 2, minH: 4 },
  { i: 'chart-breakdown',  x: 0, y: 9,  w: 2, h: 5, minW: 2, minH: 4 },
  { i: 'club-leaderboard', x: 0, y: 14, w: 2, h: 4, minW: 2, minH: 3 },
  { i: 'panel-sales',      x: 0, y: 18, w: 2, h: 4, minW: 2, minH: 3 },
  { i: 'panel-crm',        x: 0, y: 22, w: 2, h: 4, minW: 2, minH: 3 },
  { i: 'panel-pt',         x: 0, y: 26, w: 2, h: 4, minW: 2, minH: 3 },
];

// Which permission gates each card — matches the granular
// SUPER_ADMIN_*_VIEW keys the sider-menu API returns for the dashboard entry.
const PANEL_PERMISSIONS: Record<string, string> = {
  'stat-revenue':     'SUPER_ADMIN_KPI_VIEW',
  'stat-checkins':    'SUPER_ADMIN_KPI_VIEW',
  'stat-active':      'SUPER_ADMIN_KPI_VIEW',
  'stat-joins':       'SUPER_ADMIN_KPI_VIEW',
  'chart-trend':      'SUPER_ADMIN_REVENUE_TREND_VIEW',
  'chart-breakdown':  'SUPER_ADMIN_REVENUE_BREAKDOWN_VIEW',
  'club-leaderboard': 'SUPER_ADMIN_CLUB_LEADERBOARD_VIEW',
  'panel-sales':      'SUPER_ADMIN_SALES_LEADERBOARD_VIEW',
  'panel-crm':        'SUPER_ADMIN_CRM_REGISTRATIONS_VIEW',
  'panel-pt':         'SUPER_ADMIN_PT_UTILIZATION_VIEW',
};

// Bumped to v2 to drop any previously-saved layout that could overlap —
// older saves predate the minW/minH constraints above.
const LAYOUT_STORAGE_KEY = 'sad-dashboard-layout-v2';
// Separate key/grid for phone — its own 2-col positions, saved independently
// of the desktop 12-col layout above.
const MOBILE_LAYOUT_STORAGE_KEY = 'sad-dashboard-layout-mobile-v1';

const loadLayoutFrom = (key: string, fallback: LayoutItem[]): LayoutItem[] => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved) as LayoutItem[];
      // Only trust it if it still covers exactly the known widgets.
      const ids = new Set(parsed.map(l => l.i));
      if (fallback.every(d => ids.has(d.i)) && parsed.length === fallback.length) {
        return parsed;
      }
    }
  } catch {
    // ignore malformed storage
  }
  return fallback;
};

const loadLayout       = () => loadLayoutFrom(LAYOUT_STORAGE_KEY, DEFAULT_LAYOUT);
const loadMobileLayout = () => loadLayoutFrom(MOBILE_LAYOUT_STORAGE_KEY, MOBILE_LAYOUT);

/* ─── Mock Data ──────────────────────────────────────────────── */

const BREAKDOWN_PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'];

// The exact response shape for these two endpoints hasn't been confirmed yet —
// unwrap whatever array the API nests the list under, and read a few likely
// field-name variants per item.
const extractArray = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const arr = Object.values(data as object).find(v => Array.isArray(v));
    if (arr) return arr as any[];
  }
  return [];
};

const pickNum = (obj: any, keys: string[]): number => {
  for (const k of keys) if (typeof obj?.[k] === 'number') return obj[k];
  return 0;
};

const pickStr = (obj: any, keys: string[]): string => {
  for (const k of keys) if (typeof obj?.[k] === 'string') return obj[k];
  return '-';
};

const fmt = (v: number) =>
  v >= 100000 ? `₹${(v / 100000).toFixed(1)}L`
  : v >= 1000  ? `₹${(v / 1000).toFixed(0)}K`
  : `₹${v}`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="db-tooltip">
      <p className="db-tt-label">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{fmt(p.value)}</strong>
        </p>
      ))}
    </div>
  );
};

/* ─── Columns ────────────────────────────────────────────────── */

const clubColumns = [
  { title: '#',       key: 'idx',     width: 40,  render: (_: any, __: any, i: number) => i + 1 },
  { title: 'Club',    dataIndex: 'club',    key: 'club',    width: 150, ellipsis: true },
  { title: 'Type',    dataIndex: 'type',    key: 'type',    width: 100,
    render: (t: string) => <Tag color={t === 'Company' ? 'blue' : 'purple'}>{t}</Tag> },
  { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', width: 100, render: fmt },
  { title: 'Check-ins', dataIndex: 'checkins', key: 'checkins', width: 90 },
];

const salesColumns = [
  { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 60,
    render: (r: number) => (
      <span style={{ color: r <= 3 ? '#f59e0b' : 'var(--muted)', fontWeight: 700 }}>
        {r <= 3 ? ['🥇','🥈','🥉'][r-1] : r}
      </span>
    )},
  { title: 'Name',   dataIndex: 'name',   key: 'name',   width: 140, ellipsis: true },
  { title: 'Branch', dataIndex: 'branch', key: 'branch', width: 130, ellipsis: true },
  { title: 'Sales',  dataIndex: 'sales',  key: 'sales',  width: 100, render: fmt },
  { title: 'Deals',  dataIndex: 'deals',  key: 'deals',  width: 70 },
];

/* ─── Main Component ─────────────────────────────────────────── */

const formatCount = (v: number) => v.toLocaleString('en-IN');

const SuperAdminDashboard = () => {
  const { hasPermission, permissionsLoaded } = usePermissions();
  // hasPermission defaults to true while permissions are still loading, so
  // wait for permissionsLoaded too — otherwise a denied query would still
  // fire once on mount before the real (denied) permission is known.
  const canView = (perm: string) => permissionsLoaded && hasPermission(perm);
  const [clubType, setClubType] = useState('all');
  const [viewMode, setViewMode] = useState<string | number>('Monthly');
  const [expanded, setExpanded] = useState<null | 'trend' | 'breakdown'>(null);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const startDateStr = dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined;
  const endDateStr   = dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : undefined;

  // Below this width, react-grid-layout's drag/resize is impractical on
  // touch and cramming a 12-col grid into a phone just squeezes every card —
  // fall back to a plain full-width stacked list instead.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const branchId = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const { data: kpiData, isFetching: kpiLoading } = useGetSuperAdminKpiQuery({
    branchId:  selectedBranchId || undefined,
    startDate: startDateStr,
    endDate:   endDateStr,
  }, { skip: !canView(PANEL_PERMISSIONS['stat-revenue']) });

  const kpi = kpiData?.data;
  const totalRevenue  = typeof kpi?.totalRevenue  === 'number' ? kpi.totalRevenue  : null;
  const activeMembers = typeof kpi?.activeMembers === 'number' ? kpi.activeMembers : null;
  const checkinsToday = typeof kpi?.checkInsToday === 'number' ? kpi.checkInsToday : null;
  const newJoins       = typeof kpi?.newJoinees    === 'number' ? kpi.newJoinees    : null;

  const { data: breakdownData } = useGetRevenueBreakdownQuery({
    branchId:  selectedBranchId || undefined,
    startDate: startDateStr,
    endDate:   endDateStr,
  }, { skip: !canView(PANEL_PERMISSIONS['chart-breakdown']) });

  const revenueBreakdown = (breakdownData?.data?.breakdown || []).map((item, i) => ({
    name:  item.category,
    value: item.total,
    color: BREAKDOWN_PALETTE[i % BREAKDOWN_PALETTE.length],
  }));

  const trendPeriod = viewMode === 'Weekly' ? 'weekly' : viewMode === 'Yearly' ? 'yearly' : 'monthly';
  const { data: trendData } = useGetRevenueTrendQuery({
    branchId: selectedBranchId || undefined,
    period:   trendPeriod as 'weekly' | 'monthly' | 'yearly',
    year:     trendPeriod !== 'weekly' ? new Date().getFullYear() : undefined,
  }, { skip: !canView(PANEL_PERMISSIONS['chart-trend']) });

  const trendCategories = trendData?.data?.categories || [];
  const revenueTrendData = trendData?.data?.points || [];
  const trendSeries = trendCategories.map((category, i) => ({
    category,
    color: BREAKDOWN_PALETTE[i % BREAKDOWN_PALETTE.length],
  }));

  const { data: clubLeaderboardResp, isFetching: clubLeaderboardLoading } = useGetClubLeaderboardQuery({
    startDate: startDateStr,
    endDate:   endDateStr,
    limit:     10,
  }, { skip: !canView(PANEL_PERMISSIONS['club-leaderboard']) });

  const clubLeaderboardData = extractArray(clubLeaderboardResp?.data).map((item, i) => ({
    key:      String(i + 1),
    club:     pickStr(item, ['club', 'name', 'branchName', 'clubName']),
    type:     pickStr(item, ['type', 'category', 'clubType']),
    revenue:  pickNum(item, ['revenue', 'totalRevenue']),
    checkins: pickNum(item, ['checkins', 'checkIns', 'totalCheckIns']),
  }));

  const { data: salesLeaderboardResp, isFetching: salesLeaderboardLoading } = useGetSalesLeaderboardQuery({
    startDate: startDateStr,
    endDate:   endDateStr,
    limit:     10,
  }, { skip: !canView(PANEL_PERMISSIONS['panel-sales']) });

  const salesLeaderboardData = extractArray(salesLeaderboardResp?.data).map((item, i) => ({
    key:    String(i + 1),
    rank:   pickNum(item, ['rank']) || i + 1,
    name:   pickStr(item, ['name', 'salesPersonName', 'fullName']),
    branch: pickStr(item, ['branch', 'branchName']),
    sales:  pickNum(item, ['sales', 'totalSales', 'revenue', 'amount']),
    deals:  pickNum(item, ['deals', 'dealsCount', 'totalDeals', 'invoiceCount']),
  }));

  const { data: crmRegistrationsResp } = useGetCrmRegistrationsQuery({
    branchId:  selectedBranchId || undefined,
    startDate: startDateStr,
    endDate:   endDateStr,
  }, { skip: !canView(PANEL_PERMISSIONS['panel-crm']) });

  const crmRegistrationsData = extractArray((crmRegistrationsResp?.data as any)?.overall ?? crmRegistrationsResp?.data).map((item, i) => ({
    source: pickStr(item, ['source']),
    leads:  pickNum(item, ['count']),
    color:  BREAKDOWN_PALETTE[i % BREAKDOWN_PALETTE.length],
  }));
  const crmMaxLeads = Math.max(1, ...crmRegistrationsData.map(c => c.leads));

  const { data: ptUtilizationResp } = useGetPtUtilizationQuery({
    branchId: selectedBranchId || undefined,
    limit:    10,
  }, { skip: !canView(PANEL_PERMISSIONS['panel-pt']) });

  const ptUtilizationData = extractArray(ptUtilizationResp?.data).map((item, i) => ({
    branch: pickStr(item, ['branchName']),
    pct:    pickNum(item, ['utilizationPercent']),
    color:  BREAKDOWN_PALETTE[i % BREAKDOWN_PALETTE.length],
  }));

  // react-grid-layout: free 2D placement of every widget. Layout persists to
  // localStorage; dragging any card repositions it and others compact to fill.
  // Phone gets its own 2-col layout/storage, independent of desktop's 12-col one.
  const [layout, setLayout]             = useState<LayoutItem[]>(() => loadLayout());
  const [mobileLayout, setMobileLayout] = useState<LayoutItem[]>(() => loadMobileLayout());

  const handleLayoutChange = (next: readonly LayoutItem[]) => {
    const arr = next as LayoutItem[];
    setLayout(arr);
    try { localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
  };

  const handleMobileLayoutChange = (next: readonly LayoutItem[]) => {
    const arr = next as LayoutItem[];
    setMobileLayout(arr);
    try { localStorage.setItem(MOBILE_LAYOUT_STORAGE_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
  };

  const resetLayout = () => {
    if (isMobile) {
      setMobileLayout(MOBILE_LAYOUT);
      try { localStorage.removeItem(MOBILE_LAYOUT_STORAGE_KEY); } catch { /* ignore */ }
    } else {
      setLayout(DEFAULT_LAYOUT);
      try { localStorage.removeItem(LAYOUT_STORAGE_KEY); } catch { /* ignore */ }
    }
  };

  // Drop any card the current role isn't permitted to view.
  const visiblePanels       = DEFAULT_LAYOUT.filter(item => hasPermission(PANEL_PERMISSIONS[item.i]));
  const visibleIds          = new Set(visiblePanels.map(item => item.i));
  const hasAnyPanel         = visiblePanels.length > 0;
  const visibleLayout       = layout.filter(item => visibleIds.has(item.i));
  const visibleMobileLayout = mobileLayout.filter(item => visibleIds.has(item.i));

  // Render each component based on its ID
  const renderSection = (id: string) => {
    switch (id) {
      case 'stat-revenue':
        return <StatCard title="Total Revenue"   value={kpiLoading ? '—' : totalRevenue  !== null ? fmt(totalRevenue)          : 'N/A'} icon={<DollarOutlined />}  color="#6366f1" subtitle="This Month" />;
      case 'stat-checkins':
        return <StatCard title="Check-ins Today" value={kpiLoading ? '—' : checkinsToday !== null ? formatCount(checkinsToday) : 'N/A'} icon={<LoginOutlined />}   color="#10b981" subtitle="Across all clubs" />;
      case 'stat-active':
        return <StatCard title="Active Members"  value={kpiLoading ? '—' : activeMembers !== null ? formatCount(activeMembers) : 'N/A'} icon={<TeamOutlined />}    color="#f59e0b" subtitle="Total active" />;
      case 'stat-joins':
        return <StatCard title="New Joins"       value={kpiLoading ? '—' : newJoins      !== null ? formatCount(newJoins)      : 'N/A'} icon={<UserAddOutlined />} color="#ec4899" subtitle="This month" />;
      case 'chart-trend':
        return renderChartTrend();
      case 'chart-breakdown':
        return renderChartBreakdown();
      case 'club-leaderboard':
        return renderClubLeaderboard();
      case 'panel-sales':
        return renderPanelSales();
      case 'panel-crm':
        return renderPanelCRM();
      case 'panel-pt':
        return renderPanelPT();
      default:
        return null;
    }
  };

  // ── Render Functions ──

  const renderChartTrend = () => (
    <div className="sad-chart-card">
      <div className="sad-chart-head">
        <span className="sad-chart-title">Revenue Trend</span>
        {/* stopPropagation so react-grid-layout doesn't swallow these clicks */}
        <div className="sad-chart-head-actions" onMouseDown={e => e.stopPropagation()}>
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={['Weekly', 'Monthly', 'Yearly']}
            size="small"
          />
          <Button
            type="text"
            size="small"
            icon={<FullscreenOutlined />}
            onClick={() => setExpanded('trend')}
            title="Expand"
          />
        </div>
      </div>
      {renderTrendChart(220)}
    </div>
  );

  const renderChartBreakdown = () => (
    <div className="sad-chart-card">
      <div className="sad-chart-head">
        <span className="sad-chart-title">Revenue Breakdown</span>
        <Button
          type="text"
          size="small"
          icon={<FullscreenOutlined />}
          onMouseDown={e => e.stopPropagation()}
          onClick={() => setExpanded('breakdown')}
          title="Expand"
        />
      </div>
      {renderBreakdownChart(220)}
    </div>
  );

  const renderClubLeaderboard = () => (
    <div className="sad-section">
      <div className="sad-section-head">
        <TrophyOutlined style={{ color: '#f59e0b' }} />
        <span>Club Performance Leaderboard</span>
      </div>
      <Table
        columns={clubColumns}
        dataSource={clubLeaderboardData}
        loading={clubLeaderboardLoading}
        pagination={false}
        size="small"
        className="sad-table"
        scroll={{ y: 220, x: 'max-content' }}
      />
    </div>
  );

  const renderPanelSales = () => (
    <div className="sad-section sad-section-flex">
      <div className="sad-section-head">
        <RiseOutlined style={{ color: '#6366f1' }} />
        <span>Sales Leaderboard</span>
      </div>
      <Table
        columns={salesColumns}
        dataSource={salesLeaderboardData}
        loading={salesLeaderboardLoading}
        pagination={false}
        size="small"
        className="sad-table"
        scroll={{ y: 220, x: 'max-content' }}
      />
    </div>
  );

  const renderPanelCRM = () => (
    <div className="sad-section">
      <div className="sad-section-head">
        <FileTextOutlined style={{ color: '#10b981' }} />
        <span>CRM Registrations</span>
      </div>
      <div className="sad-crm-list">
        {crmRegistrationsData.map(c => (
          <div key={c.source} className="sad-crm-item">
            <span className="sad-crm-source">{c.source}</span>
            <div className="sad-crm-bar-wrap">
              <Progress
                percent={Math.round((c.leads / crmMaxLeads) * 100)}
                showInfo={false}
                strokeColor={c.color}
                trailColor="var(--hover-bg)"
                size="small"
              />
            </div>
            <span className="sad-crm-count" style={{ color: c.color }}>{c.leads}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPanelPT = () => (
    <div className="sad-section">
      <div className="sad-section-head">
        <ShopOutlined style={{ color: '#ec4899' }} />
        <span>PT Utilization</span>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(120, ptUtilizationData.length * 44)}>
        <BarChart data={ptUtilizationData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--muted)" strokeOpacity={0.2} horizontal={false} />
          <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fill: 'var(--sider-text)', fontSize: 10, opacity: 0.45 }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="branch" tick={{ fill: 'var(--sider-text)', fontSize: 11, opacity: 0.65 }} axisLine={false} tickLine={false} width={110} />
          <Tooltip formatter={(v: any) => `${v}%`} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--muted)', borderRadius: 8, color: 'var(--sider-text)' }} />
          <Bar dataKey="pct" radius={[0, 4, 4, 0]} barSize={16}>
            {ptUtilizationData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderTrendChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={revenueTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <defs>
          {trendSeries.map((s, i) => (
            <linearGradient key={s.category} id={`sad-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={s.color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--muted)" strokeOpacity={0.25} vertical={false} />
        <XAxis dataKey="label" tick={{ fill: 'var(--sider-text)', fontSize: 11, opacity: 0.55 }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={fmt} tick={{ fill: 'var(--sider-text)', fontSize: 10, opacity: 0.45 }} axisLine={false} tickLine={false} width={50} />
        <Tooltip content={<CustomTooltip />} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: 'var(--sider-text)' }} />
        {trendSeries.map((s, i) => (
          <Area key={s.category} type="monotone" dataKey={s.category} stroke={s.color} strokeWidth={2} fill={`url(#sad-grad-${i})`} dot={false} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );

  const renderBreakdownChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={revenueBreakdown} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--muted)" strokeOpacity={0.2} horizontal={false} />
        <XAxis type="number" tickFormatter={fmt} tick={{ fill: 'var(--sider-text)', fontSize: 10, opacity: 0.45 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={{ fill: 'var(--sider-text)', fontSize: 11, opacity: 0.65 }} axisLine={false} tickLine={false} width={72} />
        <Tooltip formatter={(v: any) => fmt(v)} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--muted)', borderRadius: 8, color: 'var(--sider-text)' }} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
          {revenueBreakdown.map((entry, i) => <Cell key={i} fill={entry.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className="sad-page">

      {/* ── Filters ── */}
      <div className="sad-filters">
        <div className="sad-filters-left">
          <h2 className="sad-page-title">Dashboard</h2>
          {/* <Tag color="green" style={{ fontSize: 11 }}>Owner View</Tag> */}
        </div>
        {hasAnyPanel && (
          <div className="sad-filters-right">
            <Select
              value={clubType}
              onChange={setClubType}
              style={{ width: 160 }}
              options={[
                { label: 'All Clubs',    value: 'all' },
                { label: 'Company Only', value: 'company' },
                { label: 'Franchise',    value: 'franchise' },
              ]}
            />
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
            />
            <Button className="sad-reset-btn" onClick={resetLayout}>Reset layout</Button>
          </div>
        )}
      </div>

      {hasAnyPanel ? (
        isMobile ? (
          /* ── Phone: same react-grid-layout drag-to-reorder as desktop, just
             on its own 2-col grid (stat cards 2-per-row, everything else full
             width) instead of the 12-col one — a 12-col grid squeezed onto a
             phone screen makes every column too narrow to be usable. ── */
          <div className="sad-grid-wrap sad-grid-wrap-mobile">
            <ReactGridLayout
              className="sad-grid"
              layout={visibleMobileLayout}
              onLayoutChange={handleMobileLayoutChange}
              cols={2}
              rowHeight={54}
              margin={[10, 10]}
              containerPadding={[0, 0]}
              isDraggable
              isResizable={false}
              draggableHandle=".sad-drag-handle"
            >
              {visiblePanels.map(item => (
                <div key={item.i} className="sad-grid-cell">
                  <span className="sad-drag-handle" title="Drag to move">
                    <DragOutlined />
                  </span>
                  {renderSection(item.i)}
                </div>
              ))}
            </ReactGridLayout>
          </div>
        ) : (
          /* ── Draggable dashboard grid (react-grid-layout, v1 legacy API) ── */
          <div className="sad-grid-wrap">
            <ReactGridLayout
              className="sad-grid"
              layout={visibleLayout}
              onLayoutChange={handleLayoutChange}
              cols={12}
              rowHeight={64}
              margin={[16, 16]}
              containerPadding={[0, 0]}
              isDraggable
              isResizable
              resizeHandles={['se']}
              // Drag only via the explicit grip handle — reliable, and keeps the
              // card's charts / buttons / tables fully clickable.
              draggableHandle=".sad-drag-handle"
            >
              {visiblePanels.map(item => (
                <div key={item.i} className="sad-grid-cell">
                  <span className="sad-drag-handle" title="Drag to move">
                    <DragOutlined />
                  </span>
                  {renderSection(item.i)}
                </div>
              ))}
            </ReactGridLayout>
          </div>
        )
      ) : (
        <div className="sad-empty-state">
          <TrophyOutlined className="sad-empty-icon" />
          <h3>No dashboard widgets to show — yet</h3>
          <p>Your account doesn't have access to any Super Admin widgets right now. Ask your gym admin to switch on the ones you need, and they'll show up here.</p>
        </div>
      )}

      {/* ── Expanded chart modal ── */}
      <Modal
        open={expanded !== null}
        onCancel={() => setExpanded(null)}
        footer={null}
        width="90vw"
        centered
        destroyOnHidden
        // Recharts ResponsiveContainer measures 0 width while the modal is
        // still animating open — nudge it to re-measure once it's fully open.
        afterOpenChange={(open) => { if (open) window.dispatchEvent(new Event('resize')); }}
        title={expanded === 'breakdown' ? 'Revenue Breakdown' : 'Revenue Trend'}
      >
        <div style={{ width: '100%', height: 560 }}>
          {expanded === 'breakdown'
            ? renderBreakdownChart(560)
            : renderTrendChart(560)}
        </div>
      </Modal>

    </div>
  );
};

export default SuperAdminDashboard;